import type { ReactNode } from 'react';
import { ActionDialog } from './ActionDialog';
import { AccountPicker } from './AccountPicker';
import { PlanPicker, usePayerPlan } from './PlanPicker';
import type {
  AccountHit,
  ActionState,
  CardMoveAnswer,
  CardMoveInput,
  EnterpriseQuote,
} from './pb-types';
import { day, intervalWord, planWord } from './pb-words';

export interface CardMoveDialogProps extends ActionState {
  company: string;
  quotes: EnterpriseQuote[];
  search: (q: string) => Promise<AccountHit[]>;
  today: string;
  /** The API's preview of the move asked (`preview: true`, nothing changed); null until asked. */
  preview: CardMoveAnswer | null;
  /** Asks the preview, or makes the move once the preview is shown. */
  onConfirm: (input: CardMoveInput) => void;
  /** The ask changed: the preview no longer holds. */
  onEdit: () => void;
  onClose: () => void;
}

/**
 * Move a company that pays by card into partner billing (ADR-0036, section 4): first the API's preview of the
 * card plan and the day the partner takes over, then the move. No double charge, no gap, and nothing refunded: a running
 * card plan runs to its period end, a past-due one moves now with its unpaid invoices voided. Refunds are manual.
 */
export function CardMoveDialog(p: CardMoveDialogProps): ReactNode {
  const pp = usePayerPlan(p.quotes, p.today);
  const edit =
    <T,>(f: (v: T) => void) =>
    (v: T) => (f(v), p.onEdit());
  const send = (reason: string) =>
    pp.payer &&
    p.onConfirm({
      ...{ zohoAccountId: pp.payer.zohoAccountId, ...pp.plan, when: 'renewal' },
      ...{ reason, preview: !p.preview },
    });
  return (
    <ActionDialog
      {...p}
      size="lg"
      title={`Move ${p.company} from its card plan to partner billing`}
      confirmLabel={confirmWord(p.preview, p.today)}
      ready={pp.ready}
      consequence={<MoveConsequence preview={p.preview} payer={pp.payer} company={p.company} />}
      onConfirm={send}
    >
      <AccountPicker
        label="Paying account"
        value={pp.payer}
        onChange={edit(pp.setPayer)}
        search={p.search}
      />
      <PlanPicker value={pp.plan} onChange={edit(pp.setPlan)} quotes={pp.quotes} />
    </ActionDialog>
  );
}

const confirmWord = (preview: CardMoveAnswer | null, today: string): string =>
  !preview
    ? 'Check the card plan'
    : preview.takesOverOn <= today
      ? 'Move now'
      : 'Schedule the move';

function MoveConsequence(p: {
  preview: CardMoveAnswer | null;
  payer: AccountHit | null;
  company: string;
}) {
  if (!p.preview)
    return (
      <p>
        Choose who pays and the plan, then check the card plan: nothing changes until you confirm
        the move.
      </p>
    );
  const c = p.preview.cardPlan;
  const name = p.payer?.name ?? 'The partner';
  return (
    <>
      <p>
        Card plan now: {planWord(c.package)}, {intervalWord(c.interval)}, {c.status}
        {c.periodEnd ? `, paid to ${day(c.periodEnd)}` : ''}.
      </p>
      <p>
        <strong>
          {name} pays from {day(p.preview.takesOverOn)}.
        </strong>{' '}
        The card bills nothing after its period and the partner&rsquo;s plan starts that day: no
        double charge, no gap. Until then plan changes are refused and {p.company} sees a banner.
      </p>
      <p>{p.preview.note}</p>
    </>
  );
}
